import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Form, Formik } from "formik";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { hooks } from "../hooks";
import { components } from "../components";
import AuthLayout from "../services/auth/jwt/withAuth";
import AppRadio from "../components/apps/AppRadio";
import { RadioProps } from "../types/components/Form";
import { AppSubmitButton } from "../components/apps";
import { showErrorToast, showSuccessToast } from "../components/apps/AppError";
import { UPDATE_ACCOUNT } from "../graphql/mutations/profile";
import { useQueryProfile } from "../graphql/apollo/useQuery";
import CryptoForm from "../components/profile/forms/CryptoForm";
import BankForm from "../components/profile/forms/BankForm";
import { ACCOUNT_TYPE } from "../constants/app_routes";

export const UpdateAccountScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigate = hooks.useNavigate();
  const [accountType, setAccountType] = useState<string>(ACCOUNT_TYPE.CRYPTO);
  const { refetch, myProfile } = useQueryProfile();
  const [cryptoForm, setCryptoForm] = useState({
    address: "",
    network: "",
  });

  const { pathname } = useLocation();

  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
    };

    scrollToTop();
  }, [pathname]);

  const renderHeader = (): JSX.Element => {
    return <components.Header title={t("connectBankAccount")} goBack={true} />;
  };

  const accountTypes: RadioProps[] = [
    { value: ACCOUNT_TYPE.CRYPTO, label: t("crypto") },
    { value: ACCOUNT_TYPE.BANK, label: t("bank") },
  ];
  const validationSchema = yup.object({
    cryptoNetwork: yup.string(),
    cryptoAddress: yup.string(),
  });

  const [updateAccount, { loading }] = useMutation(UPDATE_ACCOUNT, {
    onCompleted: (data) => {
      if (data.updateAccount) {
        showSuccessToast();
        refetch();
        navigate(-1);
      }
    },
    onError: (err) => {
      showErrorToast(err.message);
    },
  });

  const handleSubmit = (data: any) => {
    updateAccount({
      variables: {
        input: {
          accountType,
          accountName: data.accountName,
          accountNo: data.accountNo,
          bankName: data.bankName,
          name: data.name,
          lastname: data.lastname,
          cryptoAddress: data.cryptoAddress,
          cryptoNetwork: data.cryptoNetwork,
        },
      },
    });
  };

  const handleChangeAccountType = (e: React.ChangeEvent<HTMLInputElement>) =>
    setAccountType(e.target.value);

  const disabled = myProfile?.updatedAccount;

  useEffect(() => {
    setCryptoForm({
      network: myProfile?.account?.crypto.network,
      address: myProfile?.account?.crypto.address,
    });
    setAccountType(myProfile?.account?.accountType ?? ACCOUNT_TYPE.CRYPTO);
  }, [myProfile]);

  return (
    <AuthLayout>
      {renderHeader()}
      <main style={{ paddingBottom: 0 }} className="container">
        <div
          className="custom-block"
          style={{ padding: 20, marginTop: 50, marginBottom: 10 }}
        >
          <AppRadio
            value={accountType}
            onChange={(e: any) => handleChangeAccountType(e)}
            radios={accountTypes}
            disabled={disabled}
          />
          <Formik
            validateOnChange
            initialValues={{
              cryptoAddress: cryptoForm.address,
              cryptoNetwork: cryptoForm.network,
            }}
            onSubmit={(data: any) => handleSubmit(data)}
            validationSchema={validationSchema}
          >
            {() => (
              <Form noValidate autoComplete="off">
                {accountType === ACCOUNT_TYPE.CRYPTO ? (
                  <CryptoForm
                    disabled={disabled}
                    crypto={myProfile?.account?.crypto}
                  />
                ) : (
                  <BankForm
                    disabled={disabled}
                    bank={myProfile?.account?.bank}
                  />
                )}
                <AppSubmitButton
                  label={t("confirm")}
                  disabled={disabled}
                  loading={loading}
                />
              </Form>
            )}
          </Formik>
        </div>
      </main>
    </AuthLayout>
  );
};
